// 8-season 퍼스널 컬러 팔레트 — AI 가 type enum 만 반환하고, 코드가 이 표에서
// 추천/회피 색상을 매핑한다. 토큰 비용 절감 + 결과 일관성 보장.
//
// 사용 예:
//   import { PERSONAL_COLOR_TYPES, getPersonalColorPalette } from './personalColorPalettes';
//   const palette = getPersonalColorPalette('light_summer', 'ko');
//   palette.recommended_colors.forEach(c => render(c.hex, c.name));

export const PERSONAL_COLOR_TYPES = [
  'bright_spring',
  'warm_spring',
  'light_summer',
  'mute_summer',
  'mute_autumn',
  'deep_autumn',
  'clear_winter',
  'deep_winter',
];

// type → { name, tone, recommended_colors, avoid_colors }
// name 은 { ko, en } 객체. tone 도 마찬가지.
// 각 색상: { id, name: { ko, en }, hex }
//
// 색상 데이터는 실서비스용 기본값입니다 — 디자이너/스타일리스트 검수 후 미세 조정 권장.
export const PERSONAL_COLOR_PALETTES = {
  bright_spring: {
    name: { ko: '브라이트 스프링', en: 'Bright Spring' },
    tone: { ko: '웜·선명', en: 'Warm · Vivid' },
    skin_tone: '#F4C9A1',
    recommended_colors: [
      { id: 'coral', name: { ko: '코랄', en: 'Coral' }, hex: '#FF7F66' },
      { id: 'sunny_yellow', name: { ko: '써니 옐로우', en: 'Sunny Yellow' }, hex: '#FFD84D' },
      { id: 'leaf_green', name: { ko: '리프 그린', en: 'Leaf Green' }, hex: '#7FD15A' },
      { id: 'turquoise', name: { ko: '터쿼이즈', en: 'Turquoise' }, hex: '#3FC1C9' },
      { id: 'bright_peach', name: { ko: '브라이트 피치', en: 'Bright Peach' }, hex: '#FFB58A' },
      { id: 'ivory', name: { ko: '아이보리', en: 'Ivory' }, hex: '#FFF6E3' },
    ],
    avoid_colors: [
      { id: 'dusty_mauve', name: { ko: '더스티 모브', en: 'Dusty Mauve' }, hex: '#A07B86' },
      { id: 'charcoal_grey', name: { ko: '차콜 그레이', en: 'Charcoal Grey' }, hex: '#3A3D45' },
      { id: 'wine', name: { ko: '와인', en: 'Wine' }, hex: '#5B1F2A' },
      { id: 'cool_black', name: { ko: '쿨 블랙', en: 'Cool Black' }, hex: '#0B0B14' },
    ],
  },
  warm_spring: {
    name: { ko: '웜 스프링', en: 'Warm Spring' },
    tone: { ko: '웜·중채', en: 'Warm · Medium' },
    skin_tone: '#E8B485',
    recommended_colors: [
      { id: 'camel', name: { ko: '카멜', en: 'Camel' }, hex: '#C39564' },
      { id: 'warm_beige', name: { ko: '웜 베이지', en: 'Warm Beige' }, hex: '#E7CFA7' },
      { id: 'salmon', name: { ko: '살몬', en: 'Salmon' }, hex: '#FA8A75' },
      { id: 'mustard', name: { ko: '머스타드', en: 'Mustard' }, hex: '#D4A23A' },
      { id: 'olive', name: { ko: '올리브', en: 'Olive' }, hex: '#8C8B3D' },
      { id: 'terracotta', name: { ko: '테라코타', en: 'Terracotta' }, hex: '#D0703A' },
    ],
    avoid_colors: [
      { id: 'icy_blue', name: { ko: '아이시 블루', en: 'Icy Blue' }, hex: '#C7E3F1' },
      { id: 'fuchsia', name: { ko: '푸시아', en: 'Fuchsia' }, hex: '#D63A8C' },
      { id: 'pure_white', name: { ko: '퓨어 화이트', en: 'Pure White' }, hex: '#FFFFFF' },
      { id: 'cool_pink', name: { ko: '쿨 핑크', en: 'Cool Pink' }, hex: '#E783B7' },
    ],
  },
  light_summer: {
    name: { ko: '라이트 서머', en: 'Light Summer' },
    tone: { ko: '쿨·연한', en: 'Cool · Light' },
    skin_tone: '#F2D3CD',
    recommended_colors: [
      { id: 'ice_blue', name: { ko: '아이스 블루', en: 'Ice Blue' }, hex: '#D8ECF8' },
      { id: 'lavender', name: { ko: '라벤더', en: 'Lavender' }, hex: '#DCCFF2' },
      { id: 'dusty_pink', name: { ko: '더스티 핑크', en: 'Dusty Pink' }, hex: '#E6B8C8' },
      { id: 'soft_grey', name: { ko: '소프트 그레이', en: 'Soft Grey' }, hex: '#D9DDE2' },
      { id: 'blue_grey', name: { ko: '블루그레이', en: 'Blue-Grey' }, hex: '#AEBECC' },
      { id: 'off_white', name: { ko: '오프화이트', en: 'Off White' }, hex: '#F4F1EA' },
    ],
    avoid_colors: [
      { id: 'mustard_avoid', name: { ko: '머스타드', en: 'Mustard' }, hex: '#C49A2C' },
      { id: 'deep_orange', name: { ko: '진한 오렌지', en: 'Deep Orange' }, hex: '#E85D1C' },
      { id: 'camel_brown', name: { ko: '카멜 브라운', en: 'Camel Brown' }, hex: '#B17A45' },
      { id: 'neon', name: { ko: '형광색', en: 'Neon' }, hex: '#B6FF00' },
      { id: 'yellow_ivory', name: { ko: '노란기 강한 아이보리', en: 'Yellow Ivory' }, hex: '#F3E2B3' },
    ],
  },
  mute_summer: {
    name: { ko: '뮤트 서머', en: 'Mute Summer' },
    tone: { ko: '쿨·차분', en: 'Cool · Muted' },
    skin_tone: '#DCB8AF',
    recommended_colors: [
      { id: 'rosewood', name: { ko: '로즈우드', en: 'Rosewood' }, hex: '#9C6671' },
      { id: 'taupe', name: { ko: '토프', en: 'Taupe' }, hex: '#8C7E73' },
      { id: 'dusty_lavender', name: { ko: '더스티 라벤더', en: 'Dusty Lavender' }, hex: '#9C8FB3' },
      { id: 'slate_blue', name: { ko: '슬레이트 블루', en: 'Slate Blue' }, hex: '#6E84A1' },
      { id: 'cool_charcoal', name: { ko: '쿨 차콜', en: 'Cool Charcoal' }, hex: '#4A4F5C' },
      { id: 'misty_mauve', name: { ko: '미스티 모브', en: 'Misty Mauve' }, hex: '#B59CAA' },
    ],
    avoid_colors: [
      { id: 'vivid_orange', name: { ko: '비비드 오렌지', en: 'Vivid Orange' }, hex: '#FF6A1F' },
      { id: 'sunny_yellow_a', name: { ko: '써니 옐로우', en: 'Sunny Yellow' }, hex: '#FFD84D' },
      { id: 'lime', name: { ko: '라임', en: 'Lime' }, hex: '#C0F02A' },
      { id: 'pure_white_a', name: { ko: '퓨어 화이트', en: 'Pure White' }, hex: '#FFFFFF' },
    ],
  },
  mute_autumn: {
    name: { ko: '뮤트 어텀', en: 'Mute Autumn' },
    tone: { ko: '웜·차분', en: 'Warm · Muted' },
    skin_tone: '#D6AA80',
    recommended_colors: [
      { id: 'olive_a', name: { ko: '올리브', en: 'Olive' }, hex: '#7A7A36' },
      { id: 'mocha', name: { ko: '모카', en: 'Mocha' }, hex: '#7A5A48' },
      { id: 'mustard_b', name: { ko: '머스타드', en: 'Mustard' }, hex: '#B58A2A' },
      { id: 'sage_green', name: { ko: '세이지 그린', en: 'Sage Green' }, hex: '#8DA17A' },
      { id: 'rust', name: { ko: '러스트', en: 'Rust' }, hex: '#A35531' },
      { id: 'warm_beige_b', name: { ko: '웜 베이지', en: 'Warm Beige' }, hex: '#D9C2A0' },
    ],
    avoid_colors: [
      { id: 'fuchsia_b', name: { ko: '푸시아', en: 'Fuchsia' }, hex: '#D63A8C' },
      { id: 'icy_pink', name: { ko: '아이시 핑크', en: 'Icy Pink' }, hex: '#F7C6D9' },
      { id: 'cool_blue', name: { ko: '쿨 블루', en: 'Cool Blue' }, hex: '#3F66B6' },
      { id: 'pure_white_b', name: { ko: '퓨어 화이트', en: 'Pure White' }, hex: '#FFFFFF' },
    ],
  },
  deep_autumn: {
    name: { ko: '딥 어텀', en: 'Deep Autumn' },
    tone: { ko: '웜·깊은', en: 'Warm · Deep' },
    skin_tone: '#B5814F',
    recommended_colors: [
      { id: 'dark_brown', name: { ko: '다크 브라운', en: 'Dark Brown' }, hex: '#4A2E1F' },
      { id: 'wine_b', name: { ko: '와인', en: 'Wine' }, hex: '#6F1F2A' },
      { id: 'dark_olive', name: { ko: '다크 올리브', en: 'Dark Olive' }, hex: '#4E5A1F' },
      { id: 'pumpkin', name: { ko: '펌킨', en: 'Pumpkin' }, hex: '#B4561E' },
      { id: 'forest_green', name: { ko: '포레스트 그린', en: 'Forest Green' }, hex: '#2E5A3A' },
      { id: 'camel_b', name: { ko: '카멜', en: 'Camel' }, hex: '#A77846' },
    ],
    avoid_colors: [
      { id: 'pastel_pink', name: { ko: '파스텔 핑크', en: 'Pastel Pink' }, hex: '#FCD3E1' },
      { id: 'lavender_b', name: { ko: '라벤더', en: 'Lavender' }, hex: '#DCCFF2' },
      { id: 'neon_b', name: { ko: '형광색', en: 'Neon' }, hex: '#B6FF00' },
      { id: 'cool_grey', name: { ko: '쿨 그레이', en: 'Cool Grey' }, hex: '#C0C8D1' },
    ],
  },
  clear_winter: {
    name: { ko: '클리어 윈터', en: 'Clear Winter' },
    tone: { ko: '쿨·선명', en: 'Cool · Vivid' },
    skin_tone: '#EAC4B0',
    recommended_colors: [
      { id: 'true_white', name: { ko: '트루 화이트', en: 'True White' }, hex: '#FFFFFF' },
      { id: 'true_black', name: { ko: '트루 블랙', en: 'True Black' }, hex: '#000000' },
      { id: 'royal_blue', name: { ko: '로얄 블루', en: 'Royal Blue' }, hex: '#1F4FCC' },
      { id: 'hot_pink', name: { ko: '핫 핑크', en: 'Hot Pink' }, hex: '#F03F8C' },
      { id: 'emerald', name: { ko: '에메랄드', en: 'Emerald' }, hex: '#1C9A7C' },
      { id: 'icy_pink_b', name: { ko: '아이시 핑크', en: 'Icy Pink' }, hex: '#F7C6D9' },
    ],
    avoid_colors: [
      { id: 'mustard_c', name: { ko: '머스타드', en: 'Mustard' }, hex: '#C49A2C' },
      { id: 'camel_c', name: { ko: '카멜', en: 'Camel' }, hex: '#B17A45' },
      { id: 'beige', name: { ko: '베이지', en: 'Beige' }, hex: '#E7D7B8' },
      { id: 'olive_b', name: { ko: '올리브', en: 'Olive' }, hex: '#7A7A36' },
    ],
  },
  deep_winter: {
    name: { ko: '딥 윈터', en: 'Deep Winter' },
    tone: { ko: '쿨·깊은', en: 'Cool · Deep' },
    skin_tone: '#B5836B',
    recommended_colors: [
      { id: 'dark_navy', name: { ko: '다크 네이비', en: 'Dark Navy' }, hex: '#10193D' },
      { id: 'charcoal_b', name: { ko: '차콜', en: 'Charcoal' }, hex: '#2A2D33' },
      { id: 'wine_c', name: { ko: '와인', en: 'Wine' }, hex: '#5B1F2A' },
      { id: 'plum', name: { ko: '플럼', en: 'Plum' }, hex: '#3F1F3F' },
      { id: 'pine_green', name: { ko: '파인 그린', en: 'Pine Green' }, hex: '#1F4A3A' },
      { id: 'icy_silver', name: { ko: '아이시 실버', en: 'Icy Silver' }, hex: '#D6DBE0' },
    ],
    avoid_colors: [
      { id: 'peach', name: { ko: '피치', en: 'Peach' }, hex: '#FFB58A' },
      { id: 'mustard_d', name: { ko: '머스타드', en: 'Mustard' }, hex: '#C49A2C' },
      { id: 'beige_b', name: { ko: '베이지', en: 'Beige' }, hex: '#E7D7B8' },
      { id: 'warm_brown', name: { ko: '웜 브라운', en: 'Warm Brown' }, hex: '#8A5A3B' },
    ],
  },
};

// 안전 lookup. type 미확인 또는 unknown 이면 null 반환.
export function getPersonalColorPalette(type, lang = 'ko') {
  const p = PERSONAL_COLOR_PALETTES[type];
  if (!p) return null;
  const pickName = (obj) => (obj?.[lang] ?? obj?.ko ?? '');
  return {
    type,
    name: pickName(p.name),
    tone: pickName(p.tone),
    skin_tone: p.skin_tone,
    recommended_colors: p.recommended_colors.map((c) => ({ id: c.id, name: pickName(c.name), hex: c.hex })),
    avoid_colors: p.avoid_colors.map((c) => ({ id: c.id, name: pickName(c.name), hex: c.hex })),
  };
}

export function isValidPersonalColorType(t) {
  return PERSONAL_COLOR_TYPES.includes(t);
}
